import { useState, memo } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useCosmosEditorial } from 'providers/contexts/CosmosEditorialContext';
import { t } from 'i18next';
import InlineEditButtons from './InlineEditButtons';
import { useInlineAsset } from '../context';
import { IconHelper } from 'components/generic/IconHelper';

interface SelectedOptions {
  [key: string]: string;
}

// Define a type for the menu structure

type TMenuItem = { label?: string; value?: string };
type TMenuStructure = {
  [key: string]: TMenuItem[];
};

const InlineAssetProperties = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { editorialConfig } = useCosmosEditorial();
  const { nodeID, inlineProps, inlineRecord, setView, assetConfig, isRelatedContent } =
    useInlineAsset();
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({
    alignment: inlineProps?.node?.attrs?.alignment || 'default',
    width: inlineProps?.node?.attrs?.width || 'default',
    presentation: inlineProps?.node?.attrs?.presentation || 'default',
  });
  const inlineType = isRelatedContent
    ? t('blox.editorial.asset.related_content.label')
    : t('blox.general.asset');

  if (!editorialConfig || !assetConfig) {
    return null;
  }
  if (!editorialConfig || !assetConfig) {
    return null;
  }

  const colCount = isMobile ? 1 : isRelatedContent ? 2 : 3;

  const menuStructure: TMenuStructure = {
    width: [
      { label: 'Default', value: 'default' },
      { label: 'One-Third', value: 'one-third' },
      { label: 'Half', value: 'half' },
      { label: 'Two-Thirds', value: 'two-thirds' },
      { label: 'Full', value: 'full' },
    ],
    alignment: [
      { label: 'Default', value: 'default' },
      { label: 'Left', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'right' },
    ],
    presentation: [
      { label: 'Default', value: 'default' },
      ...(isRelatedContent
        ? [] // Skip inline_presentations if isRelContent is true
        : editorialConfig?.asset_types?.find(
            (assetType) => assetType.name === assetConfig.type,
          )?.inline_presentations || []),
    ],
  };

  const handleOptionChange = (event: SelectChangeEvent, type: string) => {
    event.preventDefault();
    setSelectedOptions(
      (prevSelectedOptions) =>
        ({
          ...prevSelectedOptions,
          [type]: event.target.value,
        }) as SelectedOptions,
    );
  };

  const onSaveClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    // Loop through selectedOptions and update values if 'default'
    const updatedOptions: SelectedOptions = {};
    Object.keys(selectedOptions).forEach((type) => {
      const value = selectedOptions[type] === 'default' ? '' : selectedOptions[type];
      updatedOptions[type] = value;
    });

    inlineProps.updateAttributes(updatedOptions);
    setView('display');
  };

  // In the renderSelect function, TypeScript now knows the type of `option`
  const renderSelect = (type: string) => (
    <FormControl
      fullWidth
      variant="outlined"
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
          backgroundColor: `${theme.palette.background.paper} !important`,
        },
      })}
    >
      <InputLabel id={`${nodeID}-${type}-label`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </InputLabel>
      <Select
        labelId={`${nodeID}-${type}-label`}
        id={`${nodeID}-${type}-select`}
        value={selectedOptions[type] === '' ? 'default' : selectedOptions[type]}
        label={type.charAt(0).toUpperCase() + type.slice(1)}
        onChange={(event) => handleOptionChange(event, type)}
      >
        {menuStructure[type].map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (!editorialConfig || !assetConfig) {
    return null;
  }

  return (
    <>
      <Tooltip title="Cancel">
        <IconButton
          aria-label="close"
          onClick={() => {
            setView('display');
          }}
          sx={{ position: 'absolute', right: '1.5rem', top: '1rem' }}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
      <Box
        data-testid="inline-asset-properties"
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: 'auto 1fr', // Two columns for title and shortcut
          gridTemplateRows: 'auto', // One row for the whole content
          alignItems: 'center', // Align items along the baseline
          gap: 2.5, // Add a gap between the two columns
          '& .MuiFormHelperText-root': {
            p: 0,
            py: 1,
            px: 1.5,
            m: 0,
            textTransform: 'none',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
          },
        })}
      >
        <IconHelper iconType={inlineRecord?.type || inlineProps.node.attrs.type} />
        <Box>
          <Box
            contentEditable={false}
            sx={{
              fontSize: '1rem',
              textTransform: 'capitalize',
            }}
          >
            {assetConfig?.type} Display Properties
          </Box>
          <Box
            contentEditable={false}
            sx={{
              fontSize: '0.75rem',
              fontWeight: '300',
              gridColumn: '1 / span 2', // Span both columns
              textTransform: 'none',
            }}
          >
            Set the display properties for this {inlineType}
          </Box>
        </Box>
        <Box sx={{ gridColumn: '1 / span 2' }}>
          <Box
            id={`${nodeID}-properties-form`}
            data-testid="inline-asset-properties-form"
            sx={{
              display: 'grid',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${colCount}, 1fr)`,
                gap: 2,
              }}
            >
              {renderSelect('alignment')}
              {renderSelect('width')}
              {!isRelatedContent && renderSelect('presentation')}
            </Box>
            <InlineEditButtons handleSaveClick={onSaveClick} />
          </Box>
        </Box>
      </Box>
    </>
  );
});

export default InlineAssetProperties;
